import { useState } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@/services/api'
import { useCurrentOrgId } from '@/hooks/useCurrentOrgId'
import type { StripeInvoice } from '@/services/api'
import { Button, Badge } from '@/components/ui'
import { formatDate } from '@/utils/format-date'

const INVOICE_PAGE_SIZE = 10

function formatCurrency(amountCents: number, currency: string): string {
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: currency.toUpperCase(),
  }).format(amountCents / 100)
}

type InvoiceStatusVariant = 'success' | 'warning' | 'danger' | 'gray'

function getInvoiceStatusVariant(status: string): InvoiceStatusVariant {
  switch (status) {
    case 'paid': return 'success'
    case 'open': return 'warning'
    case 'uncollectible':
    case 'void': return 'danger'
    default: return 'gray'
  }
}

export function InvoiceList() {
  const { orgId } = useCurrentOrgId()
  const [cursors, setCursors] = useState<string[]>([])
  const currentCursor = cursors.length > 0 ? cursors[cursors.length - 1] : undefined

  const { data, isLoading, error } = useQuery({
    queryKey: ['invoices', orgId, currentCursor],
    queryFn: () => api.listInvoices(orgId ?? '', {
      limit: INVOICE_PAGE_SIZE,
      startingAfter: currentCursor,
    }),
    enabled: !!orgId,
  })

  return (
    <div className="card">
      <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
        <h3 className="text-lg font-medium leading-6 text-gray-900">
          Invoices
        </h3>
      </div>
      <div className="px-4 py-5 sm:p-6">
        {isLoading && <InvoiceSkeleton />}
        {error && (
          <p className="text-sm text-danger-600">Failed to load invoices.</p>
        )}
        {data?.invoices.length === 0 && cursors.length === 0 && (
          <p className="text-sm text-gray-500 text-center py-4">
            No invoices yet.
          </p>
        )}
        {data && data.invoices.length > 0 && (
          <>
            <div className="overflow-x-auto">
              <table className="min-w-full divide-y divide-gray-200">
                <thead>
                  <tr>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Invoice</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Amount</th>
                    <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                    <th className="px-3 py-2 text-right text-xs font-medium text-gray-500 uppercase">PDF</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200">
                  {data.invoices.map((invoice: StripeInvoice) => (
                    <tr key={invoice.id}>
                      <td className="px-3 py-3 text-sm text-gray-900">
                        {invoice.number ?? invoice.id}
                      </td>
                      <td className="px-3 py-3 text-sm text-gray-600">
                        {formatDate(invoice.created * 1000)}
                      </td>
                      <td className="px-3 py-3 text-sm text-gray-900">
                        {formatCurrency(invoice.amountDue, invoice.currency)}
                      </td>
                      <td className="px-3 py-3">
                        <Badge variant={getInvoiceStatusVariant(invoice.status)}>
                          {invoice.status}
                        </Badge>
                      </td>
                      <td className="px-3 py-3 text-right">
                        {invoice.invoicePdf && (
                          <a
                            href={invoice.invoicePdf}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="text-sm text-primary-600 hover:text-primary-700"
                          >
                            Download
                          </a>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <div className="mt-4 flex items-center justify-between">
              <Button
                variant="secondary"
                size="sm"
                disabled={cursors.length === 0}
                onClick={() => { setCursors((prev) => prev.slice(0, -1)) }}
              >
                Previous
              </Button>
              <Button
                variant="secondary"
                size="sm"
                disabled={!data.hasMore}
                onClick={() => {
                  const lastId = data.invoices[data.invoices.length - 1].id
                  setCursors((prev) => [...prev, lastId])
                }}
              >
                Next
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

function InvoiceSkeleton() {
  return (
    <div className="animate-pulse space-y-3">
      {Array.from({ length: 3 }, (_, i) => (
        <div key={i} className="h-10 bg-gray-100 rounded" />
      ))}
    </div>
  )
}
